import { Type } from '@angular/core';
import { Route } from '@angular/router';
import {
  filterItemsByPermissions,
  IStoredUser,
  normalizeRoles,
  permissionGuard,
  resolveEffectivePermissions,
  RolePermissionMap,
} from '@dlap-ui/core';
import { APPLICATION_SERVICE_CATALOG, ServiceOption } from '../../application-workflow/service-catalog';
import { DashboardNavItem } from './dashboard-layout';

export interface PortalDashboardPageDefinition {
  path?: string;
  label: string;
  icon: string;
  permission?: string;
  showInMenu?: boolean;
  hiddenWithPermission?: string;
  loadComponent?: () => Promise<Type<unknown>>;
  children?: readonly PortalDashboardPageDefinition[];
}

export interface PortalApplicationNavigation {
  commands: string[];
  queryParams?: Record<string, string>;
  successMessage?: string;
}

export interface PortalConfig {
  appTitle: string;
  logoSrc: string;
  searchPlaceholder: string;
  defaultRedirect: string;
  rolePermissions: readonly RolePermissionMap[];
  dashboardPages: readonly PortalDashboardPageDefinition[];
  resolveApplicationNavigation: (service: ServiceOption) => PortalApplicationNavigation;
  /**
   * Catalog keys this portal offers in its "New application" modal, in the
   * order they should appear. The catalog itself spans every portal, so a
   * portal that offers only some of it lists them here. Omit to offer all.
   */
  newApplicationServiceKeys?: readonly string[];
}

/**
 * Resolves a portal's "New application" services from the shared catalog,
 * preserving the portal's own ordering.
 */
export function buildPortalApplicationServices(
  config: PortalConfig,
  userRoles?: readonly string[] | null,
): ServiceOption[] {
  const keys = config.newApplicationServiceKeys;
  const services = !keys?.length
    ? [...APPLICATION_SERVICE_CATALOG]
    : keys
        .map((key) => APPLICATION_SERVICE_CATALOG.find((service) => service.value === key))
        .filter((service): service is ServiceOption => !!service);

  const normalizedUserRoles = new Set(normalizeRoles(userRoles));

  return services.filter((service) => {
    if (!service.allowedRoles?.length) {
      return true;
    }

    return normalizeRoles(service.allowedRoles).some((role) => normalizedUserRoles.has(role));
  });
}

export function mapDashboardPagesToMenu(
  pages: readonly PortalDashboardPageDefinition[],
): readonly DashboardNavItem[] {
  return pages
    .filter((page) => page.showInMenu !== false)
    .map((page) => ({
      label: page.label,
      icon: page.icon,
      link: page.path ? `/overview/${page.path}` : '',
      permission: page.permission,
      ...(page.children ? { children: mapDashboardPagesToMenu(page.children) } : {}),
    }));
}

export function omitSupersededPages(
  pages: readonly PortalDashboardPageDefinition[],
  permissions: readonly string[],
): PortalDashboardPageDefinition[] {
  const granted = new Set(permissions.map((permission) => permission.trim().toLowerCase()));
  return pages
    .filter((page) => !page.hiddenWithPermission || !granted.has(page.hiddenWithPermission.trim().toLowerCase()))
    .map((page) => (page.children ? { ...page, children: omitSupersededPages(page.children, permissions) } : page));
}

export function buildPortalDashboardChildRoutes(config: PortalConfig): Route[] {
  const buildRoutes = (pages: readonly PortalDashboardPageDefinition[]): Route[] =>
    pages.flatMap((page) => {
      if (page.children) {
        return buildRoutes(page.children);
      }

      if (!page.path || !page.loadComponent) {
        return [];
      }

      return [
        {
          path: page.path,
          loadComponent: page.loadComponent,
          ...(page.permission
            ? { canActivate: buildPortalPermissionGuard(config, page.permission) }
            : {}),
        },
      ];
    });

  return buildRoutes(config.dashboardPages);
}

export function buildPortalPermissionGuard(config: PortalConfig, permission: string) {
  return [permissionGuard(permission, config.defaultRedirect, config.rolePermissions)];
}

export function buildPortalNavItems(
  config: PortalConfig,
  user: IStoredUser | null,
): DashboardNavItem[] {
  const permissions = resolveEffectivePermissions(user, config.rolePermissions);
  return filterItemsByPermissions(mapDashboardPagesToMenu(omitSupersededPages(config.dashboardPages, permissions)), permissions);
}

export function getPortalDefaultChildPath(config: PortalConfig): string {
  return config.defaultRedirect.replace('/overview/', '');
}
